/**
 * Provenance read from the ORIGINAL file before the upload re-encode strips
 * its EXIF. Parsing never blocks an upload; any failure degrades to "no EXIF".
 */

import exifr from 'exifr'
import { v4 as uuidv4 } from 'uuid'

import { finiteOrNull, isRecord, stringOrNull } from './parse'
import type {
  PhotoCaptureMetadata,
  PhotoClient,
  PhotoExif,
  PhotoGps,
  PhotoUploadedBy,
} from './types'

export const PHOTO_INSTALL_ID_STORAGE_KEY = 'fim-photo:install-id'

let sessionInstallId: string | null = null

type InstallIdStorage = Pick<Storage, 'getItem' | 'setItem'>

const defaultStorage = (): InstallIdStorage | null => {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}

function storedInstallId(storage: InstallIdStorage | null): string | null {
  if (!storage) return null
  try {
    const stored = storage.getItem(PHOTO_INSTALL_ID_STORAGE_KEY)
    if (stored) return stored
    const minted = uuidv4()
    storage.setItem(PHOTO_INSTALL_ID_STORAGE_KEY, minted)
    return minted
  } catch {
    return null
  }
}

/** Private windows and blocked site data throw; the fallback id lasts the page session. */
export function photoInstallId(
  storage: InstallIdStorage | null = defaultStorage()
): string {
  const stored = storedInstallId(storage)
  if (stored) return stored
  sessionInstallId ??= uuidv4()
  return sessionInstallId
}

export function photoClient(): PhotoClient {
  return {
    install_id: photoInstallId(),
    user_agent: navigator.userAgent,
    platform: navigator.platform,
    app_version: null,
  }
}

/* eslint-disable lingui/no-unlocalized-strings */
const EXIF_TAGS = [
  'Make',
  'Model',
  'Orientation',
  'DateTimeOriginal',
  'GPSLatitude',
  'GPSLongitude',
  'GPSLatitudeRef',
  'GPSLongitudeRef',
]
/* eslint-enable lingui/no-unlocalized-strings */

/** `translateValues: false` keeps `Orientation` as the numeric EXIF code. */
const EXIF_OPTIONS = {
  pick: EXIF_TAGS,
  translateValues: false,
  ifd1: false,
  interop: false,
  xmp: false,
  icc: false,
  iptc: false,
  jfif: false,
  ihdr: false,
  makerNote: false,
  userComment: false,
}

interface PhotoExifRead {
  exif: PhotoExif | null
  dateTimeOriginal: Date | null
}

const NO_EXIF: PhotoExifRead = { exif: null, dateTimeOriginal: null }

/** Cameras without a fix write 0°N 0°E rather than omitting GPS. */
function gpsOrNull(raw: Record<string, unknown>): PhotoGps | null {
  const lat = finiteOrNull(raw.latitude)
  const lon = finiteOrNull(raw.longitude)
  if (lat === null || lon === null) return null
  if (lat === 0 && lon === 0) return null
  return { lat, lon }
}

function dateOrNull(value: unknown): Date | null {
  return value instanceof Date && Number.isFinite(value.getTime())
    ? value
    : null
}

async function readPhotoExif(file: File): Promise<PhotoExifRead> {
  try {
    const raw: unknown = await exifr.parse(file, EXIF_OPTIONS)
    if (!isRecord(raw)) return NO_EXIF
    return {
      exif: {
        make: stringOrNull(raw.Make),
        model: stringOrNull(raw.Model),
        orientation: finiteOrNull(raw.Orientation),
        gps: gpsOrNull(raw),
      },
      dateTimeOriginal: dateOrNull(raw.DateTimeOriginal),
    }
  } catch {
    return NO_EXIF
  }
}

export async function readPhotoCaptureMetadata(
  file: File,
  uploader: PhotoUploadedBy | null,
  client: PhotoClient,
  now: () => Date = () => new Date()
): Promise<PhotoCaptureMetadata> {
  const uploadedAt = now().toISOString()
  const { exif, dateTimeOriginal } = await readPhotoExif(file)
  return {
    uploaded_by: uploader,
    uploaded_at: uploadedAt,
    captured_at: dateTimeOriginal?.toISOString() ?? uploadedAt,
    captured_at_source: dateTimeOriginal ? 'exif' : 'upload',
    client,
    exif,
  }
}
